import * as THREE from 'three/webgpu';
import {createSketchDimensionHistoryPatch} from '../../history/dimension/SketchDimensionHistoryAdapter';
import type {EntityPatch} from '../../history/types';
import {createExistingHistorySnapshot, createMissingHistorySnapshot} from '../../history/types';
import {SketchDimension} from '../dimension/model/SketchDimension';
import {SketchDimensionIdentityService} from '../dimension/repository/SketchDimensionIdentityService';
import type {SketchDimensionRepository} from '../dimension/repository/SketchDimensionRepository';
import type {SketchDimensionSnapshot} from '../dimension/SketchDimensionTypes';

/**
 * 草图智能尺寸剪贴板领域服务。
 *
 * 设计说明：
 * 1. 只捕获用户显式选中的尺寸，不根据已选实体自动扩张尺寸选择；
 * 2. 尺寸全部引用必须位于实体复制域，外部引用和不完整引用整体跳过；
 * 3. 粘贴时重建尺寸、参数与分组身份，并把引用重映射到新实体；
 * 4. 本服务只修改尺寸仓库并输出历史补丁，不负责 Three.js 几何复制和事务提交。
 */
export class SketchDimensionClipboardService {
    /** 与几何快照同生命周期的尺寸领域快照。 */
    private clipboardSnapshots: SketchDimensionSnapshot[] = [];

    /** 尺寸身份分配服务。 */
    private readonly identityService: SketchDimensionIdentityService;

    /**
     * @param identityService 可注入的尺寸身份服务。
     */
    public constructor(identityService = new SketchDimensionIdentityService()) {
        this.identityService = identityService;
    }

    /**
     * 捕获当前显式尺寸选择。
     *
     * @param input 选择快照、草图全部尺寸与源到剪贴板持久 ID 映射。
     * @returns 实际进入剪贴板的尺寸数量。
     */
    public capture(input: {
        readonly selectedSnapshots: readonly SketchDimensionSnapshot[];
        readonly allSketchSnapshots: readonly SketchDimensionSnapshot[];
        readonly persistentIdMap: ReadonlyMap<string, string>;
    }): number {
        const selectedIds = new Set(input.selectedSnapshots.map((snapshot) => snapshot.dimensionId));
        const ineligibleIds = new Set(
            input.selectedSnapshots
                .filter((snapshot) => !this.isReferenceClosureComplete(snapshot, input.persistentIdMap))
                .map((snapshot) => snapshot.dimensionId),
        );
        const blockedGroupIds = new Set<string>();

        /**
         * 链/坐标尺寸共享组身份，不能把部分成员伪装成彼此无关的普通尺寸。
         * 只有整组显式选中且每个成员引用闭合时，整组才允许进入剪贴板。
         */
        input.allSketchSnapshots.forEach((snapshot) => {
            const groupId = snapshot.branch.chainGroupId;
            if (!groupId) {
                return;
            }
            const groupMembers = input.allSketchSnapshots.filter(
                (candidate) => candidate.branch.chainGroupId === groupId,
            );
            const groupSelected = groupMembers.every((member) => selectedIds.has(member.dimensionId));
            const groupEligible = groupMembers.every(
                (member) => this.isReferenceClosureComplete(member, input.persistentIdMap),
            );

            if (!groupSelected || !groupEligible) {
                blockedGroupIds.add(groupId);
            }
        });

        this.clipboardSnapshots = input.selectedSnapshots
            .filter((snapshot) => !ineligibleIds.has(snapshot.dimensionId))
            .filter((snapshot) => (
                !snapshot.branch.chainGroupId || !blockedGroupIds.has(snapshot.branch.chainGroupId)
            ))
            .map((snapshot) => this.remapSnapshotReferences(snapshot, input.persistentIdMap));

        return this.clipboardSnapshots.length;
    }

    /**
     * 把尺寸剪贴板重建到目标草图。
     *
     * @param input 目标仓库、实体持久 ID 映射与草图局部平移量。
     * @returns 新尺寸 ID、完整快照与历史补丁。
     * @throws 当任一尺寸重建或写入失败时，回滚本批已创建尺寸并原样重新抛出原异常。
     */
    public paste(input: {
        readonly repository: SketchDimensionRepository;
        readonly sketchId: string;
        readonly persistentIdMap: ReadonlyMap<string, string>;
        readonly localTranslation: readonly [number, number];
    }): {
        readonly dimensionIds: readonly string[];
        readonly snapshots: readonly SketchDimensionSnapshot[];
        readonly patches: readonly EntityPatch[];
    } {
        if (this.clipboardSnapshots.length === 0) {
            return {dimensionIds: [], snapshots: [], patches: []};
        }

        const occupiedNames = new Set(
            input.repository.listBySketch(input.sketchId).map((dimension) => dimension.currentName),
        );
        const groupIdMap = new Map<string, string>();
        const createdSnapshots: SketchDimensionSnapshot[] = [];
        const patches: EntityPatch[] = [];

        try {
            this.clipboardSnapshots.forEach((clipboardSnapshot) => {
                if (!this.isReferenceClosureComplete(clipboardSnapshot, input.persistentIdMap)) {
                    return;
                }

                const dimensionId = this.identityService.allocateDimensionId();
                const name = this.identityService.allocateName(occupiedNames);
                const references = clipboardSnapshot.references.map((reference) => ({
                    ...reference,
                    entityId: input.persistentIdMap.get(reference.entityId)!,
                    ...(reference.pointId
                        ? {pointId: input.persistentIdMap.get(reference.pointId)!}
                        : {}),
                }));
                const sourceGroupId = clipboardSnapshot.branch.chainGroupId;
                let targetGroupId: string | null = null;

                if (sourceGroupId) {
                    targetGroupId = groupIdMap.get(sourceGroupId) ?? THREE.MathUtils.generateUUID();
                    groupIdMap.set(sourceGroupId, targetGroupId);
                }

                // 求解种子属于源 solver island，复制后必须由目标草图重新建立。
                const {
                    chainGroupId: _sourceGroupId,
                    solveSeedId: _sourceSolveSeedId,
                    ...stableBranch
                } = clipboardSnapshot.branch;
                const labelPosition = clipboardSnapshot.display.labelPosition;
                const snapshot: SketchDimensionSnapshot = {
                    ...clipboardSnapshot,
                    dimensionId,
                    sketchId: input.sketchId,
                    name,
                    parameter: {
                        ...clipboardSnapshot.parameter,
                        parameterId: this.identityService.createParameterId(
                            dimensionId,
                            clipboardSnapshot.parameter.quantityKind,
                        ),
                        // 表达式可能引用源草图名称或全局绑定，普通剪贴板只复制已求值数值。
                        expression: null,
                    },
                    references,
                    branch: {
                        ...stableBranch,
                        ...(targetGroupId ? {chainGroupId: targetGroupId} : {}),
                    },
                    display: {
                        ...clipboardSnapshot.display,
                        ...(labelPosition
                            ? {
                                labelPosition: [
                                    labelPosition[0] + input.localTranslation[0],
                                    labelPosition[1] + input.localTranslation[1],
                                ] as const,
                            }
                            : {}),
                    },
                    solveState: 'unknown',
                    repair: null,
                };

                occupiedNames.add(name);
                input.repository.create(SketchDimension.fromSnapshot(snapshot));
                createdSnapshots.push(snapshot);
                patches.push(createSketchDimensionHistoryPatch({
                    entityId: dimensionId,
                    operation: 'create',
                    before: createMissingHistorySnapshot(),
                    after: createExistingHistorySnapshot(snapshot),
                    metadata: {sketchId: input.sketchId},
                }));
            });
        } catch (error) {
            // 仓库变更必须原子：任一尺寸重建失败时撤销本批已创建尺寸，再由上层回滚几何快照。
            createdSnapshots.forEach((snapshot) => {
                input.repository.removeDimensionForRestore(snapshot.dimensionId);
            });
            throw error;
        }

        return {
            dimensionIds: createdSnapshots.map((snapshot) => snapshot.dimensionId),
            snapshots: createdSnapshots,
            patches,
        };
    }

    /** 清空尺寸剪贴板。 */
    public clear(): void {
        this.clipboardSnapshots = [];
    }

    /**
     * 判断尺寸引用是否完整位于当前实体复制域。
     *
     * @param snapshot 尺寸快照。
     * @param persistentIdMap 源到目标持久 ID 映射。
     * @returns 引用闭合且没有外部引用时返回 true。
     */
    private isReferenceClosureComplete(
        snapshot: SketchDimensionSnapshot,
        persistentIdMap: ReadonlyMap<string, string>,
    ): boolean {
        return snapshot.references.every((reference) => (
            !reference.externalSourcePath
            && persistentIdMap.has(reference.entityId)
            && (!reference.pointId || persistentIdMap.has(reference.pointId))
        ));
    }

    /**
     * 把尺寸引用从源草图身份映射到脱离场景的实体快照身份。
     *
     * @param snapshot 源尺寸快照。
     * @param persistentIdMap 源到快照持久 ID 映射。
     * @returns 与源仓库完全解耦的尺寸快照。
     */
    private remapSnapshotReferences(
        snapshot: SketchDimensionSnapshot,
        persistentIdMap: ReadonlyMap<string, string>,
    ): SketchDimensionSnapshot {
        return {
            ...snapshot,
            parameter: {...snapshot.parameter},
            references: snapshot.references.map((reference) => ({
                ...reference,
                entityId: persistentIdMap.get(reference.entityId)!,
                ...(reference.pointId
                    ? {pointId: persistentIdMap.get(reference.pointId)!}
                    : {}),
            })),
            branch: {...snapshot.branch},
            display: {...snapshot.display},
            repair: snapshot.repair ? {...snapshot.repair} : null,
        };
    }
}
